/* global $ */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import marketplaceApi from '../marketplaceApi';

const STATUS_TABS = ['all', 'new', 'contacted', 'no_response', 'converted', 'lost', 'duplicate'];
const STATUS_COLOR = {
  new: 'primary', contacted: 'info', no_response: 'warning',
  converted: 'success', lost: 'danger', duplicate: 'secondary',
};
const NEXT_STATUS = {
  new:         { label: 'Mark Contacted',  next: 'contacted'    },
  contacted:   { label: 'No Response',     next: 'no_response'  },
  no_response: { label: 'Re-contact',      next: 'contacted'    },
};

function timeAgo(iso) {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60)    return 'Just now';
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 30)    return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function buildWaMsg(lead, companyName) {
  // API returns name, vehicle is nested: lead.vehicle.name
  const buyerName = lead.name || 'there';
  const veh       = lead.vehicle?.name || '';
  return encodeURIComponent(
    `Hi ${buyerName},\n\nThank you for your interest in the *${veh}*.\n\nThis is ${companyName || 'us'} from PickMyWheels. When would be a good time to connect?\n\nBest regards`
  );
}

function exportCSV(leads) {
  const header = ['ID', 'Buyer', 'Phone', 'Vehicle', 'Message', 'Status', 'Note', 'Date'];
  const rows = leads.map(l => [
    l.id,
    l.name || '',
    l.phone || '',
    l.vehicle?.name || '',
    (l.message || '').replace(/,/g, ' '),
    l.status,
    (l.dealerNote || '').replace(/,/g, ' '),
    l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN') : '',
  ]);
  const csv  = [header, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url; a.download = 'leads.csv'; a.click();
  URL.revokeObjectURL(url);
}

export default function MarketplaceLeads() {
  const [leads,      setLeads]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [tab,        setTab]        = useState('all');
  const [loading,    setLoading]    = useState(true);
  const [updating,   setUpdating]   = useState(null);
  const [editNote,   setEditNote]   = useState({});   // { [id]: text }
  const [savingNote, setSavingNote] = useState(null);
  const companyName = localStorage.getItem('companyname') || '';
  const noteRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (tab !== 'all') params.status = tab;
      const { data } = await marketplaceApi.get('/leads', { params });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page, tab]);

  useEffect(() => { load(); }, [load]);

  const advance = async (lead) => {
    const ns = NEXT_STATUS[lead.status];
    if (!ns) return;
    setUpdating(lead.id);
    try {
      await marketplaceApi.patch(`/leads/${lead.id}`, { status: ns.next });
      setLeads(ls => ls.map(l => l.id === lead.id ? { ...l, status: ns.next } : l));
    } catch {
      Swal.fire('Error', 'Failed to update lead status', 'error');
    } finally { setUpdating(null); }
  };

  const markStatus = async (lead, status) => {
    setUpdating(lead.id);
    try {
      await marketplaceApi.patch(`/leads/${lead.id}`, { status });
      setLeads(ls => ls.map(l => l.id === lead.id ? { ...l, status } : l));
    } catch {
      Swal.fire('Error', 'Failed to update lead', 'error');
    } finally { setUpdating(null); }
  };

  const saveNote = async (lead) => {
    const note = editNote[lead.id] !== undefined ? editNote[lead.id] : (lead.dealerNote || '');
    setSavingNote(lead.id);
    try {
      await marketplaceApi.patch(`/leads/${lead.id}/note`, { note });
      setLeads(ls => ls.map(l => l.id === lead.id ? { ...l, dealerNote: note } : l));
      setEditNote(e => { const n = { ...e }; delete n[lead.id]; return n; });
    } catch {
      Swal.fire('Error', 'Failed to save note', 'error');
    } finally { setSavingNote(null); }
  };

  const columns = [
    {
      name: 'Buyer',
      cell: row => (
        <div>
          {/* API field: row.name */}
          <strong>{row.name || 'Unknown'}</strong>
          <div className="mt-1">
            {row.phone && (
              <>
                <a href={`tel:${row.phone}`} className="btn btn-xs btn-outline-secondary mr-1">
                  <i className="fas fa-phone mr-1" />{row.phone}
                </a>
                <a
                  href={`https://wa.me/${row.phone.replace(/\D/g, '')}?text=${buildWaMsg(row, companyName)}`}
                  target="_blank" rel="noreferrer"
                  className="btn btn-xs btn-success"
                >
                  <i className="fab fa-whatsapp mr-1" />WhatsApp
                </a>
              </>
            )}
          </div>
        </div>
      ),
      minWidth: '200px',
    },
    {
      name: 'Vehicle',
      cell: row => (
        <div>
          {/* API field: row.vehicle.name (nested object) */}
          <span>{row.vehicle?.name || '—'}</span>
          {row.vehicle?.make_year && (
            <div className="text-muted small">{row.vehicle.make_year}</div>
          )}
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: 'Message',
      cell: row => row.message
        ? <span className="small text-muted" style={{ whiteSpace: 'normal', lineHeight: 1.4 }}>{row.message}</span>
        : <span className="text-muted">—</span>,
      minWidth: '180px',
      grow: 1,
    },
    {
      name: 'Status',
      cell: row => (
        <span className={`badge badge-${STATUS_COLOR[row.status] || 'secondary'}`} style={{ textTransform: 'capitalize' }}>
          {row.status?.replace('_', ' ')}
        </span>
      ),
      width: '110px',
    },
    {
      name: 'Time',
      selector: row => timeAgo(row.createdAt),
      width: '90px',
    },
    {
      name: 'Note',
      cell: row => {
        // API field: row.dealerNote (camelCase)
        const val = editNote[row.id] !== undefined ? editNote[row.id] : (row.dealerNote || '');
        return (
          <div className="d-flex align-items-center" style={{ width: '100%' }}>
            <input
              ref={el => { noteRefs.current[row.id] = el; }}
              type="text"
              className="form-control form-control-sm"
              placeholder="Add note…"
              value={val}
              onChange={e => setEditNote(n => ({ ...n, [row.id]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') saveNote(row); }}
              style={{ minWidth: 120 }}
            />
            {editNote[row.id] !== undefined && (
              <button
                className="btn btn-xs btn-primary ml-1"
                disabled={savingNote === row.id}
                onClick={() => saveNote(row)}
              >
                {savingNote === row.id
                  ? <span className="spinner-border spinner-border-sm" />
                  : <i className="fas fa-check" />}
              </button>
            )}
          </div>
        );
      },
      minWidth: '180px',
      grow: 1,
    },
    {
      name: 'Actions',
      cell: row => {
        if (updating === row.id) {
          return <span className="spinner-border spinner-border-sm text-secondary" />;
        }
        const ns = NEXT_STATUS[row.status];
        return (
          <div className="d-flex flex-wrap gap-1">
            {ns && (
              <button onClick={() => advance(row)} className="btn btn-xs btn-outline-primary">
                {ns.label}
              </button>
            )}
            {row.status !== 'converted' && (
              <button onClick={() => markStatus(row, 'converted')} className="btn btn-xs btn-success">
                Converted
              </button>
            )}
            {row.status !== 'lost' && row.status !== 'converted' && (
              <button onClick={() => markStatus(row, 'lost')} className="btn btn-xs btn-outline-danger">
                Lost
              </button>
            )}
          </div>
        );
      },
      minWidth: '180px',
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <h1 className="m-0"><i className="fas fa-users mr-2 text-warning" />Leads</h1>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => exportCSV(leads)}>
            <i className="fas fa-download mr-1" />Export CSV
          </button>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">

          {/* Status tabs */}
          <ul className="nav nav-tabs mb-3 flex-wrap">
            {STATUS_TABS.map(t => (
              <li className="nav-item" key={t}>
                <button
                  className={`nav-link${tab === t ? ' active' : ''}`}
                  onClick={() => { setTab(t); setPage(1); }}
                  style={{ textTransform: 'capitalize' }}
                >
                  {t.replace('_', ' ')}
                </button>
              </li>
            ))}
          </ul>

          <div className="card">
            <div className="card-header">
              <span className="text-muted">{total} lead{total !== 1 ? 's' : ''}</span>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={leads}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={total}
                paginationPerPage={20}
                onChangePage={p => setPage(p)}
                noDataComponent={
                  <div className="py-5 text-center text-muted">
                    <i className="fas fa-inbox fa-2x mb-2 d-block" />
                    No leads found for this filter.
                  </div>
                }
              />
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
